/**
 * Semantic search on /search — two ways in:
 *
 *   1. TEXT (?semantic_q=...) — the search service is primary. Its direct
 *      results paginate in the main grid and related results get their own
 *      labelled region. A compact generated title/tag index is the only
 *      failure fallback.
 *   2. PHOTO (upload) — SigLIP 2 image->image via POST /similar. The bench picked
 *      image->image over image->text on 97 held-out photos: R@5 0.711 vs 0.258.
 *
 * SEARCH METHODS (the `mode` picker)
 * Every retrieval path we built is offered to the shopper, because "best on
 * average" is not "best for this shopper": someone holding a photo our photos
 * cannot match has only image->text left, and "what does it LOOK like" is a
 * question text->text cannot answer. But a picker in front of every search would
 * cost more conversions than these modes can win, so:
 *   - the DEFAULT is `auto`, which the service resolves to the measured-best path;
 *   - the picker is a COLLAPSED disclosure — invisible unless sought;
 *   - options come from GET /modes, so labels/availability live with the bench
 *     that produced them and a mode whose index failed is never offered.
 *
 * One deliberate asymmetry: on a photo upload we send `q` ONLY if the shopper
 * explicitly picked "photo + words". Otherwise a leftover query in the search box
 * would silently turn a photo search into a fusion — and fusion measurably HURTS
 * non-visual refinements ("für Zwillinge"). `auto` must mean "the best for what
 * you deliberately gave us", not "everything lying around in the page".
 *
 * Text search owns its result grid completely. Valid service empties stay empty.
 * Network, timeout, non-OK and malformed responses get one retry, then switch to
 * the generated local fallback. Request generations prevent an older query or
 * related hydration from writing into a newer result view.
 *
 * Card rendering + URL scheme validation live in search-cards.js
 * (window.DubistdaSearch) — one copy of the XSS-hardened DOM-API builder, shared
 * with visual-similar.js. Build all markup with DOM methods.
 * The card, fallback-index and fallback scripts must load first.
 */
(function () {
  // Idempotency guard: this script now loads site-wide (theme.liquid) so the header
  // camera appears on every page. If a template also loads it, the second execution
  // must no-op rather than double the controls.
  if (window.__dubistdaSearchInit) return;
  window.__dubistdaSearchInit = true;

  var RESULTS_K = 24;
  var PHOTO_K = 12;
  var RELATED_PREVIEW_K = 4;
  var FETCH_TIMEOUT_MS = 4000;
  var PHOTO_TIMEOUT_MS = 15000; // upload + SigLIP embed, much slower than a text query
  var MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

  var TEXT = {
    de: {
      label: 'Oder mit einem Foto suchen',
      hint: 'Foto auswählen — wir finden optisch ähnliche Produkte.',
      busy: 'Ähnliche Produkte werden gesucht …',
      none: 'Zu diesem Foto haben wir keine ähnlichen Produkte gefunden.',
      failed: 'Die Fotosuche ist gerade nicht verfügbar.',
      tooBig: 'Das Foto ist zu groß (max. 10 MB).',
      badType: 'Bitte ein Bild auswählen.',
      searchBusy: 'Semantische Ergebnisse werden gesucht …',
      searchEmpty: 'Keine Ergebnisse gefunden.',
      searchFailed: 'Die Suche ist gerade nicht verfügbar.',
      searchPrompt: 'Gib einen Suchbegriff ein.',
      badgeText: 'Ergebnisse: semantische Suche',
      badgeFallback: 'Fallback-Suche: lokaler Produktkatalog',
      badgePhoto: 'Ergebnisse: Fotosuche (optisch ähnlich)',
      results: function (n, q) { return n + ' Ergebnisse für „' + q + '“'; },
      fallbackResults: function (n, q) {
        return n + ' Ergebnisse für „' + q + '“ aus der lokalen Fallback-Suche.';
      },
      fallbackSwitched: 'Die Onlinesuche ist nicht erreichbar. Ergebnisse stammen jetzt aus der lokalen Fallback-Suche.',
      loadMore: 'Mehr laden',
      loadingMore: 'Weitere Ergebnisse werden geladen …',
      relatedHeading: 'Ähnliche Produkte',
      relatedResults: function (n) { return n + ' ähnliche Produkte'; },
      showAllRelated: function (n) { return 'Alle ' + n + ' ähnlichen Produkte anzeigen'; },
      showFewerRelated: 'Weniger ähnliche Produkte anzeigen',
      suggestHeadingEmpty: 'Kein direkter Treffer — vielleicht:',
      suggestHeadingMore: 'Ähnliche Suchen:',
      photoResults: function (n) { return n + (n === 1 ? ' ähnliches Produkt' : ' optisch ähnliche Produkte'); },
      channelLabel: 'Suchart',
      channelText: 'Text',
      channelPhoto: 'Foto',
      photoPrompt: 'Wähle ein Foto aus, um optisch ähnliche Produkte zu finden.',
      methodsText: 'Suchmethode wählen',
      methodsPhoto: 'Foto-Suchmethode wählen',
      methodWords: 'Worte für „Foto + Worte“',
      methodWordsHint: 'z. B. „in blau“ — beschreibt, was am Foto anders sein soll.',
      needWords: 'Bitte gib noch ein paar Worte ein (z. B. „in blau“).',
      recommended: 'empfohlen',
      drop: 'Foto hierher ziehen oder tippen zum Auswählen',
      dropHint: 'Wir finden optisch ähnliche Produkte.',
      changePhoto: 'Anderes Foto',
      cameraLabel: 'Mit Foto suchen',
    },
    en: {
      label: 'Or search with a photo',
      hint: 'Pick a photo — we’ll find visually similar products.',
      busy: 'Finding similar products …',
      none: 'We found no visually similar products for that photo.',
      failed: 'Photo search is unavailable right now.',
      tooBig: 'That photo is too large (max 10 MB).',
      badType: 'Please choose an image file.',
      searchBusy: 'Finding semantic results …',
      searchEmpty: 'No results found.',
      searchFailed: 'Search is unavailable right now.',
      searchPrompt: 'Enter a search term.',
      badgeText: 'Results: semantic search',
      badgeFallback: 'Fallback search: local product catalogue',
      badgePhoto: 'Results: photo search (visually similar)',
      results: function (n, q) { return n + ' results for “' + q + '”'; },
      fallbackResults: function (n, q) {
        return n + ' results for “' + q + '” from the local fallback search.';
      },
      fallbackSwitched: 'Online search is unreachable. Results now come from the local fallback search.',
      loadMore: 'Load more',
      loadingMore: 'Loading more results …',
      relatedHeading: 'Related products',
      relatedResults: function (n) { return n + ' related products'; },
      showAllRelated: function (n) { return 'Show all ' + n + ' related products'; },
      showFewerRelated: 'Show fewer related products',
      suggestHeadingEmpty: 'No direct match — maybe:',
      suggestHeadingMore: 'Related searches:',
      photoResults: function (n) { return n + (n === 1 ? ' similar product' : ' visually similar products'); },
      channelLabel: 'Search type',
      channelText: 'Text',
      channelPhoto: 'Photo',
      photoPrompt: 'Choose a photo to find visually similar products.',
      methodsText: 'Choose search method',
      methodsPhoto: 'Choose photo search method',
      methodWords: 'Words for “photo + words”',
      methodWordsHint: 'e.g. “in blue” — describe what should differ from the photo.',
      needWords: 'Please add a few words first (e.g. “in blue”).',
      recommended: 'recommended',
      drop: 'Drag a photo here or tap to choose',
      dropHint: 'We’ll find visually similar products.',
      changePhoto: 'Different photo',
      cameraLabel: 'Search with a photo',
    },
  };

  var api = window.DubistdaSearch;
  var fallbackApi = window.DubistdaSearchFallback;
  var t = TEXT.de;

  // Shopper-selected methods. `auto` = let the service pick the measured best.
  var textMode = 'auto';
  var photoMode = 'auto';
  var photoWords = '';   // only sent when photoMode === 'photo_text'
  var modesInfo = null;  // GET /modes payload, or null if unreachable
  var activeResultRequest = 0;
  var textState = null;

  function setStatus(message) {
    var status = document.querySelector('#SemanticSearchStatus');
    if (status) status.textContent = message;
  }

  function setBadge(label) {
    var grid = document.querySelector('#product-grid ul.product-grid');
    if (!grid || !grid.parentElement) return;
    var badge = document.querySelector('.semantic-search__badge');
    if (!badge) {
      badge = document.createElement('p');
      badge.className = 'semantic-search__badge caption';
      badge.style.marginBottom = '1rem';
      grid.parentElement.insertBefore(badge, grid);
    }
    badge.textContent = label;
  }

  /** The results <ul>, creating one if the page rendered an empty state. */
  function ensureGrid() {
    var grid = document.querySelector('#product-grid ul.product-grid');
    if (grid) return grid;
    var host = document.querySelector('#ProductGridContainer') || document.querySelector('.template-search');
    if (!host) return null;
    var wrap = document.createElement('div');
    wrap.id = 'product-grid';
    wrap.className = 'page-width';
    grid = document.createElement('ul');
    grid.className = 'grid product-grid grid--2-col-tablet-down grid--4-col-desktop';
    grid.setAttribute('role', 'list');
    wrap.appendChild(grid);
    host.appendChild(wrap);
    return grid;
  }

  function clearResults() {
    var grid = ensureGrid();
    if (grid) grid.replaceChildren();
    var badge = document.querySelector('.semantic-search__badge');
    if (badge) badge.remove();
    clearLoadMore();
    clearRelated();
    clearSuggestions();
  }

  function render(products, badgeLabel, append) {
    var grid = ensureGrid();
    if (!grid) return false;
    if (!append) grid.replaceChildren();
    products.forEach(function (p) {
      grid.appendChild(api.buildCard(p));
    });
    setBadge(badgeLabel);
    return true;
  }

  function clearLoadMore() {
    var control = document.querySelector('.semantic-search__load-more');
    if (control) control.remove();
  }

  function clearRelated() {
    var related = document.querySelector('.semantic-search__related');
    if (related) related.remove();
  }

  function clearSuggestions() {
    var box = document.querySelector('.semantic-search__suggestions');
    if (box) box.remove();
  }

  /** Validate the additive `suggestions` field: [{term, count}], strings only. */
  function sanitizeSuggestions(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.filter(function (s) {
      return s && typeof s.term === 'string' && s.term.trim().length > 0;
    });
  }

  /**
   * Clickable alternative-search chips. Prominent when the result set is empty
   * ("Kein direkter Treffer — vielleicht:"), quieter under populated results
   * ("Ähnliche Suchen:"). Each chip is a real link to the same search route with
   * a new `semantic_q`, so it is accessible, right-click-openable, and re-runs
   * the search on navigation without any internal-state coupling.
   */
  function renderSuggestions(suggestions, isEmptyResults) {
    clearSuggestions();
    var items = sanitizeSuggestions(suggestions);
    if (items.length === 0) return;

    var host = document.querySelector('#ProductGridContainer') ||
      document.querySelector('#product-grid') ||
      document.querySelector('.template-search');
    if (!host || !host.parentNode) return;

    var box = document.createElement('section');
    box.className = 'semantic-search__suggestions' +
      (isEmptyResults ? ' semantic-search__suggestions--prominent' : '');

    var heading = document.createElement('p');
    heading.className = 'semantic-search__suggestions-heading caption';
    heading.textContent = isEmptyResults ? t.suggestHeadingEmpty : t.suggestHeadingMore;
    box.appendChild(heading);

    var list = document.createElement('ul');
    list.className = 'semantic-search__suggestions-list';
    list.setAttribute('role', 'list');
    var basePath = window.location.pathname;
    items.forEach(function (s) {
      var li = document.createElement('li');
      var chip = document.createElement('a');
      chip.className = 'semantic-search__chip';
      chip.href = basePath + '?semantic_q=' + encodeURIComponent(s.term);
      chip.textContent = s.term;                 // text-only DOM assignment (XSS-safe)
      li.appendChild(chip);
      list.appendChild(li);
    });
    box.appendChild(list);

    // Empty state: put chips right where results would be. Populated: below the grid.
    if (isEmptyResults) {
      host.parentNode.insertBefore(box, host.nextSibling);
    } else {
      host.parentNode.appendChild(box);
    }
  }

  function setRelatedExpanded(region, expanded) {
    var expandedClass = 'semantic-search__related--expanded';
    var classes = region.className.split(/\s+/).filter(Boolean).filter(function (name) {
      return name !== expandedClass;
    });
    if (expanded) classes.push(expandedClass);
    region.className = classes.join(' ');
  }

  function ensureRelated() {
    var existing = document.querySelector('.semantic-search__related');
    if (existing) return existing;
    var primary = document.querySelector('#ProductGridContainer') || document.querySelector('#product-grid');
    if (!primary || !primary.parentNode) return null;

    var region = document.createElement('section');
    region.className = 'semantic-search__related page-width';
    region.setAttribute('aria-labelledby', 'SemanticSearchRelatedHeading');

    var heading = document.createElement('h2');
    heading.id = 'SemanticSearchRelatedHeading';
    heading.className = 'h2';
    heading.textContent = t.relatedHeading;

    var count = document.createElement('p');
    count.className = 'semantic-search__related-count caption';

    var grid = document.createElement('ul');
    grid.id = 'SemanticSearchRelatedGrid';
    grid.className =
      'semantic-search__related-grid grid product-grid grid--2-col-tablet-down grid--4-col-desktop';
    grid.setAttribute('role', 'list');

    region.appendChild(heading);
    region.appendChild(count);
    region.appendChild(grid);
    primary.parentNode.insertBefore(region, primary.nextSibling);
    return region;
  }

  // ── method picker ────────────────────────────────────────────────────────────

  /** GET /modes — the service is authoritative on what exists and what works. */
  async function fetchModes() {
    try {
      var resp = await api.fetchWithTimeout(api.API_BASE + '/modes', FETCH_TIMEOUT_MS);
      if (!resp.ok) return null;
      return await resp.json();
    } catch (e) {
      return null; // no picker; `auto` still works. Never block search on this.
    }
  }

  /**
   * A collapsed <details> with one radio per available mode.
   *
   * Radios rather than a <select> so each option can carry its own help line —
   * "By look" means nothing without "matches your words against the photos".
   * Built with DOM APIs (createElement + textContent) because the labels arrive
   * over the network; see the XSS rules in search-cards.js.
   *
   * @param kinds  which `needs` values belong in this picker
   * @param onPick called with the chosen mode id
   */
  function buildModePicker(summaryText, kinds, groupName, current, onPick) {
    if (!modesInfo || !modesInfo.modes) return null;
    var choices = modesInfo.modes.filter(function (m) {
      return m.available && (m.id === 'auto' || kinds.indexOf(m.needs) !== -1);
    });
    if (choices.length < 2) return null; // nothing to choose between

    var details = document.createElement('details');
    details.className = 'semantic-search__modes';
    details.style.cssText = 'margin-top:1rem;text-align:left;max-width:34rem;margin-left:auto;margin-right:auto;';

    var summary = document.createElement('summary');
    summary.className = 'caption';
    summary.textContent = summaryText;
    summary.style.cssText = 'cursor:pointer;opacity:0.75;';
    details.appendChild(summary);

    var list = document.createElement('div');
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', summaryText);
    list.style.cssText = 'margin-top:0.75rem;display:grid;gap:0.6rem;';

    var lang = api.currentLang();
    choices.forEach(function (m) {
      var row = document.createElement('label');
      row.style.cssText = 'display:grid;grid-template-columns:auto 1fr;gap:0.5rem;align-items:start;cursor:pointer;';

      var radio = document.createElement('input');
      radio.setAttribute('type', 'radio');
      radio.setAttribute('name', groupName);
      radio.setAttribute('value', m.id);
      if (m.id === current) radio.setAttribute('checked', 'checked');
      radio.addEventListener('change', function () {
        if (radio.checked) onPick(m.id);
      });

      var textWrap = document.createElement('div');
      var title = document.createElement('span');
      title.textContent = (m.label && m.label[lang]) || m.id;
      if (m.recommended) title.style.cssText = 'font-weight:600;';
      textWrap.appendChild(title);

      if (m.recommended && m.id !== 'auto') {
        var tag = document.createElement('span');
        tag.className = 'caption';
        tag.textContent = ' (' + t.recommended + ')';
        tag.style.cssText = 'opacity:0.7;';
        textWrap.appendChild(tag);
      }

      var help = document.createElement('span');
      help.className = 'caption';
      help.textContent = (m.help && m.help[lang]) || '';
      help.style.cssText = 'display:block;opacity:0.7;';
      textWrap.appendChild(help);

      row.appendChild(radio);
      row.appendChild(textWrap);
      list.appendChild(row);
    });

    details.appendChild(list);
    return details;
  }

  // ── paginated service search + local failure fallback ───────────────────────

  function isCount(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function isCursor(value) {
    return value === null || (
      typeof value === 'string' &&
      value.length > 0 &&
      value.length <= 4096 &&
      !/[\u0000-\u0020\u007f]/.test(value)
    );
  }

  function emptyMap() {
    return Object.create(null);
  }

  function isResultList(value) {
    return Array.isArray(value) && value.every(function (result) {
      return result && typeof result === 'object' &&
        typeof result.handle === 'string' &&
        /^[a-z0-9][a-z0-9-]{0,254}$/.test(result.handle);
    });
  }

  function validIndexInfo(value) {
    return value && typeof value === 'object' &&
      typeof value.revision === 'string' && value.revision.length > 0 &&
      isCount(value.n_products) &&
      (value.built_at === null || typeof value.built_at === 'string');
  }

  /**
   * Accept the Wave 2 envelope and the old top-level `results` response.
   * A top-level result list is exactly one direct page: it has no invented
   * continuation and no related products.
   */
  function normalizeSearchResponse(payload) {
    if (!payload || typeof payload !== 'object') return null;
    // The additive backend contract deliberately retains top-level `results`
    // for old clients. Presence of `direct` therefore identifies the new
    // envelope and must win over that legacy alias.
    if (Object.prototype.hasOwnProperty.call(payload, 'direct')) {
      var direct = payload.direct;
      var related = payload.related;
      if (
        !direct || typeof direct !== 'object' ||
        !related || typeof related !== 'object' ||
        !isResultList(direct.results) ||
        !isCount(direct.total) ||
        direct.total < direct.results.length ||
        !isCursor(direct.next_cursor) ||
        (direct.results.length === 0 && direct.next_cursor !== null) ||
        !isResultList(related.results) ||
        !isCount(related.total) ||
        related.total < related.results.length ||
        !validIndexInfo(payload.index)
      ) {
        return null;
      }
      return {
        direct: {
          next_cursor: direct.next_cursor,
          results: direct.results,
          total: direct.total,
        },
        index: payload.index,
        related: {
          results: related.results,
          total: related.total,
        },
        suggestions: sanitizeSuggestions(payload.suggestions),
      };
    }
    if (!Array.isArray(payload.results) || !isResultList(payload.results)) return null;
    var legacyTotal = payload.total == null ? payload.results.length : payload.total;
    var legacyCursor = payload.next_cursor == null ? null : payload.next_cursor;
    if (!isCount(legacyTotal) || legacyTotal < payload.results.length ||
        !isCursor(legacyCursor) ||
        (payload.results.length === 0 && legacyCursor !== null)) return null;
    return {
      direct: {
        next_cursor: legacyCursor,
        results: payload.results,
        total: legacyTotal,
      },
      index: null,
      related: { results: [], total: 0 },
      suggestions: sanitizeSuggestions(payload.suggestions),
    };
  }

  function backendSearchUrl(q, lang, mode, cursor) {
    var url = api.API_BASE + '/search?q=' + encodeURIComponent(q) +
      '&k=' + RESULTS_K +
      '&lang=' + encodeURIComponent(lang) +
      '&mode=' + encodeURIComponent(mode);
    if (cursor !== null) url += '&cursor=' + encodeURIComponent(cursor);
    return url;
  }

  /** One initial request plus at most one retry, including malformed JSON data. */
  async function fetchBackendPage(q, lang, mode, cursor, requestId) {
    var lastError = null;
    for (var attempt = 0; attempt < 2; attempt += 1) {
      if (requestId !== activeResultRequest) return null;
      try {
        var response = await api.fetchWithTimeout(
          backendSearchUrl(q, lang, mode, cursor),
          FETCH_TIMEOUT_MS
        );
        if (!response.ok) throw new Error('search returned HTTP ' + response.status);
        var normalized = normalizeSearchResponse(await response.json());
        if (!normalized) throw new Error('search returned a malformed response');
        return normalized;
      } catch (error) {
        lastError = error;
      }
    }
    if (requestId !== activeResultRequest) return null;
    console.warn('[semantic-search] primary search failed after retry', lastError);
    throw lastError || new Error('search failed');
  }

  function fallbackCard(product) {
    return fallbackApi ? fallbackApi.cardProduct(product, api.storefrontPath) : null;
  }

  /**
   * Preserve service order. Storefront product JSON supplies the richest card,
   * while the compact allowlist fills any missing hydration by handle.
   */
  async function materialize(results) {
    if (results.length === 0) return [];
    var hydrated = await api.hydrate(results);
    var byHandle = emptyMap();
    hydrated.forEach(function (product) {
      if (product && typeof product.handle === 'string') byHandle[product.handle] = product;
    });
    return results.map(function (result) {
      var product = byHandle[result.handle];
      if (product) {
        product.url = api.storefrontPath('products/' + encodeURIComponent(result.handle));
        return product;
      }
      var compact = fallbackApi && fallbackApi.productByHandle(result.handle);
      return compact ? fallbackCard(compact) : null;
    }).filter(Boolean);
  }

  function uniqueUnseen(results, seen) {
    var pageSeen = emptyMap();
    return results.filter(function (result) {
      if (seen[result.handle] || pageSeen[result.handle]) return false;
      pageSeen[result.handle] = true;
      return true;
    });
  }

  async function renderRelated(results, total, requestId) {
    if (requestId !== activeResultRequest || !textState || textState.source !== 'backend') return;
    if (results.length === 0 || total === 0) {
      clearRelated();
      return;
    }
    var filtered = uniqueUnseen(results, textState.seen);
    var products = await materialize(filtered);
    if (requestId !== activeResultRequest || !textState || textState.source !== 'backend') return;
    products = products.filter(function (product) {
      return product && !textState.seen[product.handle];
    });
    if (products.length === 0) {
      clearRelated();
      return;
    }
    var region = ensureRelated();
    if (!region) return;
    var grid = region.querySelector('.semantic-search__related-grid');
    var count = region.querySelector('.semantic-search__related-count');
    var oldActions = region.querySelector('.semantic-search__related-actions');
    if (oldActions) oldActions.remove();
    region.className = region.className
      .split(/\s+/)
      .filter(Boolean)
      .filter(function (name) {
        return name !== 'semantic-search__related--within-desktop-preview';
      })
      .join(' ');
    if (products.length <= RELATED_PREVIEW_K) {
      region.className += ' semantic-search__related--within-desktop-preview';
    }
    setRelatedExpanded(region, false);
    grid.replaceChildren();
    products.forEach(function (product) {
      grid.appendChild(api.buildCard(product));
    });
    // The backend total is pre-dedupe. Report what is visibly rendered after
    // direct-result exclusion and hydration so the label never over-claims.
    count.textContent = t.relatedResults(products.length);
    if (products.length > 2) {
      var actions = document.createElement('div');
      actions.className = 'semantic-search__related-actions';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.textContent = t.showAllRelated(products.length);
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', grid.id);
      button.addEventListener('click', function () {
        var expanded = region.className.indexOf('semantic-search__related--expanded') === -1;
        setRelatedExpanded(region, expanded);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? t.showFewerRelated : t.showAllRelated(products.length);
      });
      actions.appendChild(button);
      region.appendChild(actions);
    }
  }

  function stableNextCursor(current, next, cursorHistory) {
    if (
      !isCursor(next) ||
      next === null ||
      next === current ||
      Object.prototype.hasOwnProperty.call(cursorHistory, next)
    ) return null;
    cursorHistory[next] = true;
    return next;
  }

  /**
   * A continuation belongs to the exact ranking snapshot that produced page one.
   * If a service restarts on a new index (or changes its query total), replacing
   * the grid with the deterministic fallback is safer than interleaving ranks.
   */
  function backendPageMatchesState(page, state) {
    if (!page || !state || state.source !== 'backend') return false;
    var revision = page.index ? page.index.revision : null;
    return revision === state.indexRevision && page.direct.total === state.total;
  }

  function mountLoadMore(requestId) {
    clearLoadMore();
    if (!textState || textState.requestId !== requestId || textState.cursor === null) return;
    var grid = ensureGrid();
    if (!grid || !grid.parentElement) return;
    var wrap = document.createElement('div');
    wrap.className = 'semantic-search__load-more';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary';
    button.textContent = t.loadMore;
    button.setAttribute('aria-controls', 'product-grid');
    button.addEventListener('click', function () {
      loadMore(requestId, button);
    });
    wrap.appendChild(button);
    grid.parentElement.appendChild(wrap);
  }

  function setStateFromFallback(q, requestId, switched, lang, mode) {
    if (!fallbackApi) return false;
    var page;
    try {
      page = fallbackApi.search(q, { limit: RESULTS_K });
    } catch (error) {
      return false;
    }
    textState = {
      cursor: page.next_cursor,
      cursorHistory: emptyMap(),
      lang: lang,
      mode: mode,
      query: q,
      requestId: requestId,
      seen: emptyMap(),
      source: 'fallback',
      total: page.total,
    };
    if (page.next_cursor !== null) textState.cursorHistory[page.next_cursor] = true;
    var products = page.results.map(fallbackCard).filter(Boolean);
    page.results.forEach(function (result) { textState.seen[result.handle] = true; });
    clearResults();
    if (products.length > 0) render(products, t.badgeFallback, false);
    else setBadge(t.badgeFallback);
    setStatus(
      switched ?
        t.fallbackSwitched + ' ' + t.fallbackResults(page.total, q) :
        t.fallbackResults(page.total, q)
    );
    mountLoadMore(requestId);
    return true;
  }

  async function showBackendFirstPage(q, page, requestId, lang, mode) {
    if (requestId !== activeResultRequest) return;
    var direct = uniqueUnseen(page.direct.results, emptyMap());
    var products = await materialize(direct);
    if (requestId !== activeResultRequest) return;

    var cursorHistory = emptyMap();
    textState = {
      cursor: stableNextCursor(null, page.direct.next_cursor, cursorHistory),
      cursorHistory: cursorHistory,
      indexRevision: page.index ? page.index.revision : null,
      lang: lang,
      mode: mode,
      query: q,
      requestId: requestId,
      seen: emptyMap(),
      source: 'backend',
      total: page.direct.total,
    };
    if (textState.cursor !== null) textState.cursorHistory[textState.cursor] = true;
    // Dedupe by authoritative result handle, including a direct hit whose
    // optional card hydration could not be materialized.
    direct.forEach(function (result) { textState.seen[result.handle] = true; });

    clearResults();
    if (page.direct.results.length === 0) {
      setStatus(t.searchEmpty);
      renderSuggestions(page.suggestions, true); // prominent: turn the dead end into a doorway
      return; // a valid empty is authoritative; never invoke the fallback
    }
    if (products.length === 0 || !render(products, t.badgeText, false)) {
      setStatus(t.searchFailed);
      return;
    }
    setStatus(t.results(page.direct.total, q));
    mountLoadMore(requestId);
    renderRelated(page.related.results, page.related.total, requestId);
    renderSuggestions(page.suggestions, false); // quieter "Ähnliche Suchen" under results
  }

  async function appendBackendPage(page, requestId) {
    if (requestId !== activeResultRequest || !textState) return;
    if (!backendPageMatchesState(page, textState)) {
      throw new Error('search continuation changed ranking snapshot');
    }
    var candidates = uniqueUnseen(page.direct.results, textState.seen);
    var products = await materialize(candidates);
    if (requestId !== activeResultRequest || !textState || textState.source !== 'backend') return;
    candidates.forEach(function (result) { textState.seen[result.handle] = true; });
    render(products, t.badgeText, true);
    textState.total = page.direct.total;
    textState.cursor = stableNextCursor(
      textState.cursor,
      page.direct.next_cursor,
      textState.cursorHistory
    );
    setStatus(t.results(textState.total, textState.query));
    mountLoadMore(requestId);
  }

  async function appendFallbackPage(requestId) {
    if (requestId !== activeResultRequest || !textState || !fallbackApi) return;
    var page = fallbackApi.search(textState.query, {
      cursor: textState.cursor,
      limit: RESULTS_K,
    });
    var products = page.results.map(fallbackCard).filter(Boolean).filter(function (product) {
      return !textState.seen[product.handle];
    });
    page.results.forEach(function (result) { textState.seen[result.handle] = true; });
    render(products, t.badgeFallback, true);
    textState.total = page.total;
    textState.cursor = stableNextCursor(
      textState.cursor,
      page.next_cursor,
      textState.cursorHistory
    );
    setStatus(t.fallbackResults(textState.total, textState.query));
    mountLoadMore(requestId);
  }

  async function loadMore(requestId, button) {
    if (
      requestId !== activeResultRequest ||
      !textState ||
      textState.requestId !== requestId ||
      textState.cursor === null
    ) return;
    button.disabled = true;
    button.textContent = t.loadingMore;
    if (textState.source === 'fallback') {
      await appendFallbackPage(requestId);
      return;
    }

    try {
      var page = await fetchBackendPage(
        textState.query,
        textState.lang,
        textState.mode,
        textState.cursor,
        requestId
      );
      if (!page || requestId !== activeResultRequest) return;
      await appendBackendPage(page, requestId);
    } catch (error) {
      if (requestId !== activeResultRequest) return;
      // A source switch replaces the complete grid. This keeps totals, ordering
      // and cursor ownership honest instead of silently mixing two rankers.
      if (!setStateFromFallback(
        textState.query,
        requestId,
        true,
        textState.lang,
        textState.mode
      )) {
        setStatus(t.searchFailed);
      }
    }
  }

  async function runTextSearch(q) {
    var requestId = ++activeResultRequest;
    textState = null;
    setStatus(t.searchBusy);
    clearResults();
    var lang = api.currentLang();
    var mode = textMode;

    try {
      var page = await fetchBackendPage(
        q,
        lang,
        mode,
        null,
        requestId
      );
      if (!page || requestId !== activeResultRequest) return;
      await showBackendFirstPage(q, page, requestId, lang, mode);
    } catch (error) {
      if (requestId !== activeResultRequest) return;
      if (!setStateFromFallback(q, requestId, false, lang, mode)) {
        clearResults();
        setStatus(t.searchFailed);
      }
    }
  }

  // ── photo -> image->image ────────────────────────────────────────────────────
  async function runPhotoSearch(file, hint) {
    if (file.type && file.type.indexOf('image/') !== 0) {
      hint.textContent = t.badType;
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      hint.textContent = t.tooBig;
      return;
    }
    // 'photo_text' is the one mode the service will reject without words. Say so
    // plainly here rather than letting it 400 into a generic "unavailable".
    if (photoMode === 'photo_text' && !photoWords.trim()) {
      hint.textContent = t.needWords;
      return;
    }
    var requestId = ++activeResultRequest;
    textState = null;
    clearLoadMore();
    clearRelated();
    hint.textContent = t.busy;

    var payload = new FormData();
    payload.append('file', file);
    payload.append('k', String(PHOTO_K));
    payload.append('mode', photoMode);
    // Only ever send words when the shopper explicitly asked to combine them with
    // the photo. Sending a stray search-box query here would silently fuse a plain
    // photo search — and fusion measurably hurts non-visual refinements.
    if (photoMode === 'photo_text' && photoWords.trim()) {
      payload.append('q', photoWords.trim());
    }

    var data;
    try {
      var resp = await api.fetchWithTimeout(api.API_BASE + '/similar', PHOTO_TIMEOUT_MS, {
        method: 'POST',
        body: payload,
      });
      if (!resp.ok) {
        if (requestId !== activeResultRequest) return;
        hint.textContent = t.failed;
        return;
      }
      data = await resp.json();
    } catch (e) {
      if (requestId !== activeResultRequest) return;
      console.warn('[semantic-search] photo search failed', e);
      hint.textContent = t.failed;
      return;
    }

    if (requestId !== activeResultRequest) return;
    var results = (data && data.results) || [];
    if (results.length === 0) {
      hint.textContent = t.none;
      return;
    }
    var products = await api.hydrate(results);
    if (requestId !== activeResultRequest) return;
    if (products.length === 0) {
      hint.textContent = t.none;
      return;
    }

    if (!render(products, t.badgePhoto)) {
      hint.textContent = t.failed;
      return;
    }
    hint.textContent = '';
    setStatus(t.photoResults(products.length));
  }

  // Method picker as a SEGMENTED PILL control (the C redesign) — replaces the
  // collapsed radio-list disclosure. Reads the same /modes payload; each pill is a
  // method, the selected one is filled, and a help line explains it. Returns null
  // if there's nothing to choose between (so a single-method setup shows no picker).
  function buildSegmentedPicker(kinds, current, onPick) {
    if (!modesInfo || !modesInfo.modes) return null;
    var choices = modesInfo.modes.filter(function (m) {
      return m.available && (m.id === 'auto' || kinds.indexOf(m.needs) !== -1);
    });
    if (choices.length < 2) return null;
    var lang = api.currentLang();
    var box = document.createElement('div');
    var seg = document.createElement('div');
    seg.className = 'ss-seg';
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute(
      'aria-label',
      kinds.indexOf('text') !== -1 ? t.methodsText : t.methodsPhoto
    );
    var help = document.createElement('div');
    help.className = 'ss-seg__help';
    var btns = [];
    function pick(id, m) {
      btns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-id') === id)); });
      help.textContent = (m.help && m.help[lang]) || '';
      onPick(id);
    }
    choices.forEach(function (m) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ss-seg__btn';
      b.setAttribute('data-id', m.id);
      b.textContent = (m.label && m.label[lang]) || m.id;
      b.setAttribute('aria-pressed', String(m.id === current));
      b.addEventListener('click', function () { pick(m.id, m); });
      if (m.id === current) help.textContent = (m.help && m.help[lang]) || '';
      seg.appendChild(b);
      btns.push(b);
    });
    box.appendChild(seg);
    box.appendChild(help);
    return box;
  }

  // Wrap a method picker in a COLLAPSED <details> so the technical options are
  // hidden by default (Christine: "a bit technical for shoppers"), while every
  // option stays one click away (Antreas: "keep all options for a special case").
  // "Auto" is the working default, so search works without ever opening this.
  function collapseMethods(summaryText, node) {
    if (!node) return null;
    var d = document.createElement('details');
    d.className = 'ss-methods';
    var s = document.createElement('summary');
    s.className = 'ss-methods__summary';
    s.textContent = summaryText;
    d.appendChild(s);
    d.appendChild(node);
    return d;
  }

  // One <style> block for the whole photo-search UI (C+A). Injected once — keeps the
  // markup builders clean and the visual language consistent, brand-tokened off Dawn's
  // CSS custom properties so it matches whatever theme colours are set.
  var STYLES_ID = 'semantic-search-styles';
  function injectStyles() {
    if (document.getElementById(STYLES_ID)) return;
    var css =
      '.ss-photo{margin:2rem auto 0;max-width:34rem;text-align:center;clear:both}' +
      '.ss-channel{display:flex;justify-content:center;gap:.35rem;margin:1rem auto 0}' +
      '.ss-channel__btn{min-width:7rem;min-height:4.4rem;border:1px solid rgba(var(--color-foreground),.22);' +
      'background:rgb(var(--color-background));color:rgb(var(--color-foreground));border-radius:999px;' +
      'padding:.65rem 1.2rem;font:inherit;font-weight:600;cursor:pointer}' +
      '.ss-channel__btn[aria-pressed=true]{background:rgb(141 111 175);color:#fff;border-color:transparent}' +
      '.ss-photo__panel{margin-top:1.25rem}' +
      '.ss-photo__head{font-weight:600;font-size:1.6rem;color:rgb(var(--color-foreground));margin-bottom:.7rem}' +
      '.ss-drop{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'min-height:158px;gap:.25rem;border:2px dashed rgba(var(--color-foreground),.3);border-radius:14px;' +
      'padding:1.5rem;cursor:pointer;transition:border-color .15s,background .15s;background:rgb(var(--color-background))}' +
      '.ss-drop:hover,.ss-drop--over{border-color:rgb(141 111 175);background:rgba(141 111 175,.05)}' +
      '.ss-drop__icon{font-size:2rem;line-height:1}' +
      '.ss-drop__text{font-size:1.5rem;font-weight:500;color:rgb(var(--color-foreground))}' +
      '.ss-drop__hint{font-size:1.4rem;opacity:.72}' +
      '.ss-drop input[type=file]{position:absolute;width:1px;height:1px;opacity:0;overflow:hidden;clip:rect(0 0 0 0)}' +
      '.ss-preview{display:none;align-items:center;gap:.75rem;justify-content:center;margin-top:.9rem}' +
      '.ss-preview img{width:64px;height:64px;object-fit:cover;border-radius:8px;border:1px solid rgba(var(--color-foreground),.15)}' +
      '.ss-preview__meta{font-size:1.4rem;text-align:left}' +
      '.ss-change{background:none;border:none;color:rgb(141 111 175);cursor:pointer;font:inherit;font-size:1.4rem;padding:.5rem 0;text-decoration:underline}' +
      '.ss-spin{width:16px;height:16px;border:2px solid rgba(var(--color-foreground),.25);border-top-color:rgb(141 111 175);border-radius:50%;display:inline-block;vertical-align:-3px;margin-right:.4rem;animation:ss-rot .7s linear infinite}' +
      '@keyframes ss-rot{to{transform:rotate(360deg)}}' +
      '.ss-seg{display:flex;flex-wrap:wrap;gap:.4rem;justify-content:center;margin-top:1rem}' +
      '.ss-seg__btn{border:1px solid rgba(var(--color-foreground),.2);background:rgb(var(--color-background));color:rgb(var(--color-foreground));' +
      'border-radius:999px;padding:.65rem 1rem;font:inherit;font-size:1.4rem;cursor:pointer;transition:background .12s,border-color .12s}' +
      '.ss-seg__btn:hover{border-color:rgb(141 111 175)}' +
      '.ss-seg__btn[aria-pressed=true]{background:rgb(141 111 175);color:rgb(255 255 255);border-color:transparent}' +
      '.ss-seg__help{font-size:1.4rem;opacity:.78;margin-top:.4rem;min-height:1.1em}' +
      '.ss-words{display:none;margin-top:.7rem}' +
      '.ss-words input{width:100%;max-width:24rem}' +
      // Christine: keep "Auto" the default and don't confront shoppers with the
      // technical method buttons — so the pill picker (C) lives in a collapsed
      // disclosure. Clean search by default; one click reveals every option.
      '.ss-methods{max-width:34rem;margin:.9rem auto 0;text-align:center}' +
      '.ss-methods__summary{cursor:pointer;font-size:1.4rem;color:rgba(var(--color-foreground),.78);' +
      'display:inline-flex;align-items:center;gap:.35rem;list-style:none;padding:.25rem .5rem}' +
      '.ss-methods__summary::-webkit-details-marker{display:none}' +
      '.ss-methods__summary::before{content:"›";display:inline-block;font-weight:700;transition:transform .15s}' +
      '.ss-methods[open]>.ss-methods__summary::before{transform:rotate(90deg)}' +
      '.ss-methods__summary:hover{color:rgb(var(--color-foreground))}' +
      '.ss-methods .ss-seg{margin-top:.5rem}' +
      '.semantic-search__load-more{display:flex;justify-content:center;margin:2.5rem 0 1rem}' +
      '.semantic-search__related{margin-top:4rem;margin-bottom:2rem}' +
      '.semantic-search__related-count{margin-top:-.5rem;margin-bottom:1.5rem}' +
      '.semantic-search__related-actions{display:flex;justify-content:center;margin-top:2rem}' +
      '.semantic-search__related:not(.semantic-search__related--expanded) .semantic-search__related-grid>.grid__item:nth-child(n+5){display:none}' +
      '@media(max-width:749px){.semantic-search__related:not(.semantic-search__related--expanded) .semantic-search__related-grid>.grid__item:nth-child(n+3){display:none}}' +
      '@media(min-width:750px){.semantic-search__related--within-desktop-preview .semantic-search__related-actions{display:none}}' +
      // A: camera sits INSIDE the search field, just left of the magnifier — a clean
      // circular icon button, vertically centred, absolutely positioned so it never
      // collides with the absolute submit button.
      '.search .field{position:relative}' +
      '.ss-camera{position:absolute;right:48px;top:50%;transform:translateY(-50%);width:34px;height:34px;' +
      'display:inline-flex;align-items:center;justify-content:center;padding:0;border:none;background:none;' +
      'border-radius:50%;cursor:pointer;color:rgb(141 111 175);z-index:2;transition:background .12s}' +
      '.ss-camera:hover{background:rgba(141,111,175,.14)}' +
      '.ss-camera svg{width:20px;height:20px;display:block}';
    var el = document.createElement('style');
    el.id = STYLES_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }

  // ── C: the full search-page photo experience ────────────────────────────────
  function mountPhotoControl(photoRequested, textQuery) {
    // Mount in the search TEMPLATE, as a sibling AFTER the header — NOT inside
    // .template-search__search, where the "N results" status text lives and would
    // overlap the drop zone. Fall back gracefully if the structure differs.
    var tpl = document.querySelector('.template-search');
    var header = document.querySelector('.template-search__header');
    var host = tpl || header;
    if (!host) return;

    var wrap = document.createElement('div');
    wrap.className = 'ss-photo';

    var channel = document.createElement('div');
    channel.className = 'ss-channel';
    channel.setAttribute('role', 'group');
    channel.setAttribute('aria-label', t.channelLabel);
    var textButton = document.createElement('button');
    textButton.type = 'button';
    textButton.className = 'ss-channel__btn';
    textButton.textContent = t.channelText;
    textButton.setAttribute('aria-controls', 'SemanticPhotoPanel');
    var photoButton = document.createElement('button');
    photoButton.type = 'button';
    photoButton.className = 'ss-channel__btn';
    photoButton.textContent = t.channelPhoto;
    photoButton.setAttribute('aria-controls', 'SemanticPhotoPanel');
    channel.appendChild(textButton);
    channel.appendChild(photoButton);
    wrap.appendChild(channel);

    var photoPanel = document.createElement('div');
    photoPanel.id = 'SemanticPhotoPanel';
    photoPanel.className = 'ss-photo__panel';
    function showPhotoChannel(showPhoto) {
      photoPanel.hidden = !showPhoto;
      textButton.setAttribute('aria-pressed', String(!showPhoto));
      photoButton.setAttribute('aria-pressed', String(showPhoto));
    }
    textButton.addEventListener('click', function () {
      showPhotoChannel(false);
      ++activeResultRequest;
      textState = null;
      if (textQuery) runTextSearch(textQuery);
      else {
        clearResults();
        setStatus(t.searchPrompt);
      }
      var queryInput = document.querySelector('input[name="semantic_q"]');
      if (queryInput) queryInput.focus();
    });
    photoButton.addEventListener('click', function () {
      showPhotoChannel(true);
      ++activeResultRequest;
      textState = null;
      clearResults();
      setStatus(t.photoPrompt);
      photoPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    var head = document.createElement('div');
    head.className = 'ss-photo__head';
    head.textContent = t.label;
    photoPanel.appendChild(head);

    // the branded drag-and-drop zone (replaces the raw file input). Flex column,
    // block children — no <br>, so nothing overlaps.
    var drop = document.createElement('label');
    drop.className = 'ss-drop';
    drop.setAttribute('for', 'SemanticPhotoInput');
    var icon = document.createElement('span'); icon.className = 'ss-drop__icon'; icon.textContent = '📷';
    var dtext = document.createElement('span'); dtext.className = 'ss-drop__text'; dtext.textContent = t.drop;
    var dhint = document.createElement('span'); dhint.className = 'ss-drop__hint'; dhint.textContent = t.dropHint;
    var input = document.createElement('input');
    input.setAttribute('type', 'file'); input.setAttribute('accept', 'image/*'); input.setAttribute('id', 'SemanticPhotoInput');
    drop.appendChild(icon); drop.appendChild(dtext); drop.appendChild(dhint); drop.appendChild(input);
    photoPanel.appendChild(drop);

    // thumbnail preview of the chosen photo (hidden until one is picked)
    var preview = document.createElement('div');
    preview.className = 'ss-preview';
    var pimg = document.createElement('img'); pimg.setAttribute('alt', '');
    var pmeta = document.createElement('div'); pmeta.className = 'ss-preview__meta';
    var change = document.createElement('button');
    change.type = 'button'; change.className = 'ss-change'; change.textContent = t.changePhoto;
    pmeta.appendChild(change);
    preview.appendChild(pimg); preview.appendChild(pmeta);
    photoPanel.appendChild(preview);

    // status line for search progress/results — starts EMPTY (the drop zone already
    // shows the "we'll find similar products" hint; duplicating it read as broken).
    var hint = document.createElement('p');
    hint.className = 'ss-seg__help'; hint.setAttribute('role', 'status');
    hint.textContent = '';
    photoPanel.appendChild(hint);

    // words field for "photo + words" (revealed only when that method is chosen)
    var words = document.createElement('div');
    words.className = 'ss-words';
    var wordsInput = document.createElement('input');
    wordsInput.setAttribute('type', 'text'); wordsInput.setAttribute('id', 'SemanticPhotoWords');
    wordsInput.className = 'field__input';
    wordsInput.setAttribute('placeholder', t.methodWordsHint);
    wordsInput.addEventListener('input', function () { photoWords = wordsInput.value; });
    words.appendChild(wordsInput);
    photoPanel.appendChild(words);

    var lastFile = null, objUrl = null;
    function onFile(file) {
      if (!file) return;
      lastFile = file;
      if (objUrl) URL.revokeObjectURL(objUrl);
      objUrl = URL.createObjectURL(file);
      pimg.setAttribute('src', objUrl);
      preview.style.display = 'flex';
      runPhotoSearch(file, hint);
    }
    input.addEventListener('change', function () {
      if (input.files && input.files[0]) onFile(input.files[0]);
    });
    change.addEventListener('click', function (e) { e.preventDefault(); input.click(); });

    // real drag-and-drop onto the zone
    ['dragenter', 'dragover'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('ss-drop--over'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('ss-drop--over'); });
    });
    drop.addEventListener('drop', function (e) {
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) onFile(f);
    });

    // C: the method picker as a SEGMENTED control (not a hidden radio list)
    var picker = buildSegmentedPicker(['photo', 'both'], photoMode, function (id) {
      photoMode = id;
      words.style.display = id === 'photo_text' ? 'block' : 'none';
      if (lastFile && (id !== 'photo_text' || photoWords.trim())) runPhotoSearch(lastFile, hint);
    });
    var pickerBox = collapseMethods(t.methodsPhoto, picker);
    if (pickerBox) photoPanel.appendChild(pickerBox);
    wrap.appendChild(photoPanel);

    wordsInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && lastFile) { e.preventDefault(); runPhotoSearch(lastFile, hint); }
    });

    // Insert AFTER the header (clean space below the search field + status), not
    // inside the search box where the results-count text would overlap it.
    if (header && header.parentNode) header.parentNode.insertBefore(wrap, header.nextSibling);
    else host.appendChild(wrap);

    // A entry point: /search?photo=1 opens straight onto the photo zone
    showPhotoChannel(photoRequested);
    if (photoRequested) {
      wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      drop.classList.add('ss-drop--over');
      setTimeout(function () { drop.classList.remove('ss-drop--over'); }, 1200);
    }
  }

  // ── A: a camera icon inside the header search, on every page ─────────────────
  // Clicking it takes the shopper to the search page opened on the photo zone.
  function mountHeaderCamera() {
    if (document.querySelector('.ss-camera')) return; // once
    // Dawn's header search form (both the inline and the drawer variants)
    var forms = document.querySelectorAll('form[action$="/search"], .search-modal form, .header__search form');
    forms.forEach(function (form) {
      if (form.querySelector('.ss-camera')) return;
      if (form.closest('.template-search')) return; // the /search page has the full zone already
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ss-camera';
      btn.setAttribute('aria-label', t.cameraLabel);
      btn.setAttribute('title', t.cameraLabel);
      // crisp SVG camera built with createElementNS; it inherits currentColor
      var NS = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
      var path = document.createElementNS(NS, 'path');
      path.setAttribute('d', 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z');
      var circ = document.createElementNS(NS, 'circle');
      circ.setAttribute('cx', '12'); circ.setAttribute('cy', '13'); circ.setAttribute('r', '4');
      svg.appendChild(path); svg.appendChild(circ);
      btn.appendChild(svg);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var base = api.storefrontPath('search');
        window.location.href = base + '?photo=1';
      });
      // place it inside the .field (shares the positioning context with the magnifier)
      var field = form.querySelector('.field') || form;
      field.appendChild(btn);
    });
  }

  /** The text-method picker (segmented), mounted next to the results it governs. */
  function mountTextModePicker(q) {
    var host = document.querySelector('.template-search__search') || document.querySelector('.template-search__header');
    if (!host) return;
    var picker = buildSegmentedPicker(['text'], textMode, function (id) {
      textMode = id;
      runTextSearch(q); // re-rank the same query through the newly chosen method
    });
    var pickerBox = collapseMethods(t.methodsText, picker);
    if (pickerBox) host.appendChild(pickerBox);
  }

  async function run() {
    if (!api || !api.API_BASE) return; // shared helper missing/not configured — inert by design
    t = TEXT[api.currentLang()] || TEXT.de;
    injectStyles();

    // A: the camera in the header search runs on EVERY page (needs no /modes fetch).
    mountHeaderCamera();

    // The rest is the /search-page experience — only there is there a search template.
    var onSearchPage = document.querySelector('.template-search');
    if (!onSearchPage) return;

    var params = new URLSearchParams(window.location.search);
    // Fall back to Shopify's own ?q=. The header form and every native search link
    // submit `q`, and this layer REPLACES the server-rendered product grid — so
    // reading only `semantic_q` meant a header search rendered 48 results and then
    // had them wiped, leaving "Gib einen Suchbegriff ein" on a page whose title
    // still said "48 Ergebnisse gefunden". Empty results for the customer, on the
    // most-used path into the catalogue.
    var q = (params.get('semantic_q') || params.get('q') || '').trim();
    var queryInput = document.querySelector('input[name="semantic_q"]');
    if (queryInput) queryInput.value = q;

    // Honour ?mode= so a chosen method survives reload and can be linked/shared.
    // Validated against /modes below; an unknown value simply stays `auto`.
    var urlMode = params.get('mode');

    // Fetch modes BEFORE mounting, so pickers render in one pass rather than
    // popping in. Failure is fine: no pickers, `auto` everywhere, search works.
    modesInfo = await fetchModes();
    if (urlMode && modesInfo && modesInfo.modes) {
      var m = modesInfo.modes.filter(function (x) { return x.id === urlMode && x.available; })[0];
      if (m && (m.needs === 'text' || m.id === 'auto')) textMode = urlMode;
    }

    var photoRequested = params.get('photo') === '1';
    mountPhotoControl(photoRequested, q);
    if (q && !photoRequested) {
      mountTextModePicker(q);
      runTextSearch(q);
    } else if (photoRequested) {
      clearResults();
      setStatus(t.photoPrompt);
    } else {
      clearResults();
      setStatus(t.searchPrompt);
    }
  }

  // Contract tests opt in before evaluating this asset. Nothing is exported in
  // the storefront, keeping the runtime surface as small as the pre-Wave-2 code.
  if (window.__DUBISTDA_SEARCH_TEST__) {
    window.DubistdaSemanticSearchTest = {
      backendPageMatchesState: backendPageMatchesState,
      backendSearchUrl: backendSearchUrl,
      beginRequest: function () { return ++activeResultRequest; },
      fetchBackendPage: fetchBackendPage,
      getTextState: function () { return textState; },
      loadMore: loadMore,
      normalizeSearchResponse: normalizeSearchResponse,
      runTextSearch: runTextSearch,
      stableNextCursor: stableNextCursor,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
